import { useEffect, useRef, useState } from 'react';
import { Bug, Camera, CheckCircle2, Paperclip, X } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { bugContext, bugReportsEnabled, bugReportUi, errorLogFile, openBugReport, recentErrors } from '@/lib/bugReport';
import { desktop } from '@/lib/desktop';
import { formatBytes } from '@/lib/format';
import { errorMessage } from '@/lib/toast';
import { Button, Field, Input, Modal, Textarea } from './ui';

const MAX_FILES = 3;
const MAX_FILE_BYTES = 8 * 1024 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,video/mp4,video/webm,text/plain,.log,.txt,.json,.csv';

/** Whether this server takes bug reports (the entry points hide themselves when not). */
export function useBugReportsEnabled(): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    let live = true;
    void bugReportsEnabled().then((v) => live && setOn(v));
    return () => {
      live = false;
    };
  }, []);
  return on;
}

/** A small bug button for toolbars (the editor's top bar). */
export function BugReportButton() {
  if (!useBugReportsEnabled()) return null;
  return (
    <Button variant="ghost" size="icon-sm" title="Report a bug" aria-label="Report a bug" onClick={openBugReport} data-open-bug-report>
      <Bug size={15} />
    </Button>
  );
}

/** Captures one frame of this tab (the browser asks which screen to share). */
async function captureScreen(): Promise<File> {
  const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false, preferCurrentTab: true } as DisplayMediaStreamOptions);
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.srcObject = stream;
    await video.play();
    // Give the page a moment to repaint without the dialog.
    await new Promise((r) => setTimeout(r, 300));
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
    if (!blob) throw new Error('Could not capture the screen');
    return new File([blob], 'screenshot.png', { type: 'image/png' });
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

const canCapture = !desktop && typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

/** Ids in the address, so the server can say which organization and project the report is about. */
function whereAmI(): { orgId?: string; projectId?: string } {
  const org = /^\/orgs\/([^/]+)/.exec(location.pathname);
  const project = /^\/p\/([^/]+)/.exec(location.pathname);
  return { orgId: org?.[1], projectId: project?.[1] };
}

/** The dialog itself; mounted once for the whole app, opened with openBugReport(). */
export function BugReportDialog() {
  const open = bugReportUi((s) => s.open);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [includeLog, setIncludeLog] = useState(true);
  const [busy, setBusy] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState('');
  const [filed, setFiled] = useState<{ ref: string; grouped: boolean } | null>(null);
  const idempotencyKey = useRef('');
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    idempotencyKey.current = crypto.randomUUID();
    setError('');
  }, [open]);

  const close = () => {
    bugReportUi.setState({ open: false });
    setFiled(null);
  };
  const reset = () => {
    setTitle('');
    setDescription('');
    setFiles([]);
    setIncludeLog(true);
  };

  const addFiles = (incoming: File[]) => {
    setError('');
    const next = [...files];
    for (const f of incoming) {
      if (next.length >= MAX_FILES) return setError(`Attach at most ${MAX_FILES} files.`);
      if (f.size > MAX_FILE_BYTES) {
        setError(`${f.name} is larger than ${formatBytes(MAX_FILE_BYTES)}.`);
        continue;
      }
      next.push(f.name === 'image.png' ? new File([f], `pasted-${next.length + 1}.png`, { type: f.type }) : f);
    }
    setFiles(next);
  };

  const capture = async () => {
    setCapturing(true);
    try {
      addFiles([await captureScreen()]);
    } catch (e) {
      if ((e as Error).name !== 'NotAllowedError') setError(errorMessage(e));
    } finally {
      setCapturing(false);
    }
  };

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      const report = {
        title: title.trim(),
        description: description.trim(),
        errors: includeLog ? recentErrors() : '',
        context: bugContext(),
        ...whereAmI(),
        idempotencyKey: idempotencyKey.current,
      };
      const form = new FormData();
      form.set('report', JSON.stringify(report));
      // The log counts towards the attachment limit; the person's own files go first.
      const log = includeLog ? errorLogFile() : null;
      for (const f of log && files.length < MAX_FILES ? [...files, log] : files) form.append('file', f, f.name);
      const res = await fetch('/api/bug-reports', { method: 'POST', credentials: 'include', body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new ApiError(data.error || res.statusText, res.status);
      setFiled({ ref: data.ref, grouped: !!data.grouped });
      reset();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (filed) {
    return (
      <Modal open={open} onClose={close} title="Thanks for the report" footer={<Button variant="primary" onClick={close}>Done</Button>}>
        <div className="flex items-start gap-3 text-sm">
          <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-ok" />
          <div className="text-muted">
            {filed.grouped ? 'Someone has reported this already; your report was added to theirs' : 'It is on its way to the team'}
            {filed.ref ? (
              <>
                {' '}
                as <b className="text-fg">{filed.ref}</b>
              </>
            ) : null}
            . Mention that reference if you get in touch about it.
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open && !capturing}
      onClose={close}
      title="Report a bug"
      width={520}
      footer={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!title.trim()} onClick={() => void send()} data-send-bug-report>
            Send report
          </Button>
        </>
      }
    >
      <div
        className="space-y-3"
        onPaste={(e) => {
          const pasted = [...e.clipboardData.files];
          if (pasted.length) {
            e.preventDefault();
            addFiles(pasted);
          }
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          addFiles([...e.dataTransfer.files]);
        }}
      >
        <Field label="What went wrong?">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="e.g. Export stops at 99%" autoFocus />
        </Field>
        <Field label="Details" hint="optional">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            maxLength={10_000}
            placeholder={'What did you do, what did you expect, and what happened instead?\nYou can paste a screenshot here.'}
          />
        </Field>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => fileInput.current?.click()} disabled={files.length >= MAX_FILES}>
              <Paperclip size={13} /> Attach a file
            </Button>
            {canCapture ? (
              <Button size="sm" onClick={() => void capture()} disabled={files.length >= MAX_FILES} title="Take a screenshot of this tab">
                <Camera size={13} /> Screenshot
              </Button>
            ) : null}
            <span className="text-xs text-faint">
              Up to {MAX_FILES} images, videos or logs, {formatBytes(MAX_FILE_BYTES)} each
            </span>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => {
                addFiles([...(e.target.files ?? [])]);
                e.target.value = '';
              }}
            />
          </div>
          {files.length ? (
            <ul className="mt-2 space-y-1">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-2 rounded-lg border border-line bg-panel-2 px-2.5 py-1.5 text-xs">
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <span className="text-faint">{formatBytes(f.size)}</span>
                  <button className="text-faint hover:text-fg" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <label className="flex items-start gap-2 text-xs text-muted">
          <input type="checkbox" className="mt-0.5" checked={includeLog} onChange={(e) => setIncludeLog(e.target.checked)} />
          <span>Include the errors this page has logged since it opened</span>
        </label>
        <p className="text-xs text-faint">
          The report is sent with your name and email, the page you are on, and your browser, system and graphics card, so the team can follow up and reproduce it.
        </p>
        {error ? <p className="text-sm text-danger">{error}</p> : null}
      </div>
    </Modal>
  );
}
